import { useAuth } from "@clerk/clerk-react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { blue, green, grey, orange } from "@mui/material/colors";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BentoBrand,
  BentoBrandPartnership,
  BentoContactResponse,
  BentoSearchFilter as BentoSearchFilterType,
  COMPANY_TYPES,
  COMPENSATION_TYPES,
  CONTACT_TYPES,
  FOLLOWING_SIZES,
  FollowingSizeRange,
  LOCATIONS,
  TARGET_AGE_RANGES,
  TARGET_GENDERS,
  TargetAgeRange,
} from "schemas/dashboard";
import { Message, MessageAttachment } from "schemas/email";
import { SetAlertType } from "schemas/functions";

import EmailThread from "components/EmailThread";
import LocationAutocomplete from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import { fetcherAuth } from "utils/api";
import { openBase64DataInTab } from "utils/files";
import { formatNumber, generateInitials } from "utils/string";

interface BrandContact {
  id: number;
  name: string;
  title: string;
  email: string;
  source: string;
  best_response_ranking: number | null;
  best_response_type: string | null;
  last_sent_to_at: number | null;
}

interface RecentResponse {
  best_response_ranking: number;
  best_response_type: string;
  timestamp: number;
}

interface SearchBrand extends BentoBrand {
  contacts?: BrandContact[];
  contactedAmount?: number;
  nicheness?: number;
  isLocalOnly?: boolean;
  recentResponses?: RecentResponse[];
  // snake_to_camel converts "_score" to "Score"
  Score?: number;
}

interface NicheAutomation {
  id: number;
  status: string;
  isTopNiche: boolean;
  searchParameters: Record<string, unknown> | null;
}

interface Niche {
  id: number;
  name: string;
  status: string;
  locationName: string | null;
  automations?: NicheAutomation[];
}

type SearchType = "automations" | "repitching" | "saved_brands";
type SearchMode = "niches" | "custom";

interface SearchFilterItem {
  id: number;
  name: string;
}

interface Geopoint {
  country_code: string;
  latitude?: number;
  longitude?: number;
  label?: string;
  is_country?: boolean;
}

interface BrandSearchParameters {
  query?: string;
  category_ids?: number[];
  company_types?: string[];
  contact_types?: string[];
  locations?: string[];
  geopoints?: string[];
  min_instagram_followers?: number;
  max_instagram_followers?: number;
  compensation_types?: string[];
  target_genders?: string[];
  min_target_age?: number;
  max_target_age?: number;
}

interface AutomationCard {
  id: string;
  nicheName?: string;
  params: BrandSearchParameters;
}

interface BentoCategory {
  id: number;
  name: string;
  subcategories?: { id: number; name: string; displayName?: string }[];
}

interface BrandSearchDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: number;
  niches: Niche[];
  setAlert: SetAlertType;
  includedSearchFilters: SearchFilterItem[];
  excludedSearchFilters: SearchFilterItem[];
}

export default function BrandSearchDialog({
  open,
  onClose,
  organizationId,
  niches,
  setAlert,
  includedSearchFilters,
  excludedSearchFilters,
}: BrandSearchDialogProps) {
  const { getToken } = useAuth();

  const [searchType, setSearchType] = useState<SearchType>("automations");
  const [counterMetricsDisabled, setCounterMetricsDisabled] = useState(false);
  const [neverContactedBrandsOnly, setNeverContactedBrandsOnly] =
    useState(false);
  const [alreadyEmailedDisabled, setAlreadyEmailedDisabled] = useState(false);
  const [selectedNicheIds, setSelectedNicheIds] = useState<number[]>(
    niches.map((n) => n.id),
  );
  const [limit, setLimit] = useState(100);

  // All available VA search filters (fetched once)
  const [allVaSearchFilters, setAllVaSearchFilters] = useState<
    BentoSearchFilterType[]
  >([]);

  // Separate included/excluded selected IDs
  const [includedFilterIds, setIncludedFilterIds] = useState<number[]>(
    includedSearchFilters.map((f) => f.id),
  );
  const [excludedFilterIds, setExcludedFilterIds] = useState<number[]>(
    excludedSearchFilters.map((f) => f.id),
  );

  const fetchVaSearchFilters = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        "/api/admin/bento-search-filters",
        "GET",
      );
      const filters: BentoSearchFilterType[] = res.bentoSearchFilters || [];
      setAllVaSearchFilters(
        filters.filter((f) => f.enableForVaInclude || f.enableForVaExclude),
      );
    } catch {
      // non-critical
    }
  };

  useEffect(() => {
    if (open && allVaSearchFilters.length === 0) {
      fetchVaSearchFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const includeFilters = allVaSearchFilters.filter((f) => f.enableForVaInclude);
  const excludeFilters = allVaSearchFilters.filter((f) => f.enableForVaExclude);

  const toggleIncluded = (filterId: number) => {
    setIncludedFilterIds((prev) =>
      prev.includes(filterId)
        ? prev.filter((id) => id !== filterId)
        : [...prev, filterId],
    );
  };

  const toggleExcluded = (filterId: number) => {
    setExcludedFilterIds((prev) =>
      prev.includes(filterId)
        ? prev.filter((id) => id !== filterId)
        : [...prev, filterId],
    );
  };

  const resetFilters = () => {
    setIncludedFilterIds(includedSearchFilters.map((f) => f.id));
    setExcludedFilterIds(excludedSearchFilters.map((f) => f.id));
  };

  const hasFilterOverrides = () => {
    const origIncluded = new Set(includedSearchFilters.map((f) => f.id));
    const origExcluded = new Set(excludedSearchFilters.map((f) => f.id));
    const currIncluded = new Set(includedFilterIds);
    const currExcluded = new Set(excludedFilterIds);
    if (origIncluded.size !== currIncluded.size) return true;
    if (origExcluded.size !== currExcluded.size) return true;
    for (const id of origIncluded) if (!currIncluded.has(id)) return true;
    for (const id of origExcluded) if (!currExcluded.has(id)) return true;
    return false;
  };

  // Custom automations mode state
  const [searchMode, setSearchMode] = useState<SearchMode>("niches");
  const [automationCards, setAutomationCards] = useState<AutomationCard[]>([]);
  const [categories, setCategories] = useState<BentoCategory[]>([]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        "/api/admin/bento-categories",
        "GET",
      );
      setCategories(res.topLevelCategories || []);
    } catch {
      // non-critical
    }
  }, [getToken]);

  const buildCardsFromNiches = useCallback(() => {
    const cards: AutomationCard[] = [];
    for (const niche of niches) {
      if (!niche.automations) continue;
      for (const auto of niche.automations) {
        if (auto.status !== "active" || !auto.searchParameters) continue;
        const sp = auto.searchParameters;
        cards.push({
          id: `niche-${niche.id}-auto-${auto.id}`,
          nicheName: niche.name,
          params: {
            query: (sp.query as string) || undefined,
            category_ids: (sp.category_ids as number[]) || [],
            company_types: (sp.company_types as string[]) || ["all"],
            contact_types: (sp.contact_types as string[]) || ["all"],
            locations: (sp.locations as string[]) || ["all"],
            geopoints: (sp.geopoints as string[]) || undefined,
            min_instagram_followers:
              (sp.min_instagram_followers as number) || undefined,
            max_instagram_followers:
              (sp.max_instagram_followers as number) || undefined,
            compensation_types:
              (sp.compensation_types as string[]) || undefined,
            target_genders: (sp.target_genders as string[]) || undefined,
            min_target_age: (sp.min_target_age as number) || undefined,
            max_target_age: (sp.max_target_age as number) || undefined,
          },
        });
      }
    }
    return cards;
  }, [niches]);

  const handleSwitchMode = (newMode: SearchMode) => {
    if (newMode === searchMode) return;
    setSearchMode(newMode);
    if (newMode === "custom") {
      setAutomationCards(buildCardsFromNiches());
      if (categories.length === 0) fetchCategories();
    }
  };

  const addAutomationCard = () => {
    setAutomationCards((prev) => [
      ...prev,
      {
        id: `custom-${Date.now()}`,
        params: {
          company_types: ["all"],
          contact_types: ["all"],
          locations: ["all"],
        },
      },
    ]);
  };

  const removeAutomationCard = (cardId: string) => {
    setAutomationCards((prev) => prev.filter((c) => c.id !== cardId));
  };

  const updateAutomationCard = (
    cardId: string,
    patch: Partial<BrandSearchParameters>,
  ) => {
    setAutomationCards((prev) =>
      prev.map((c) =>
        c.id === cardId ? { ...c, params: { ...c.params, ...patch } } : c,
      ),
    );
  };

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchBrand[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [queryObject, setQueryObject] = useState<any>(null);

  const handleSearch = async () => {
    if (searchMode === "niches" && selectedNicheIds.length === 0) {
      setAlert("Please select at least one niche", "warning");
      return;
    }
    if (searchMode === "custom" && automationCards.length === 0) {
      setAlert("Please add at least one automation card", "warning");
      return;
    }
    setLoading(true);
    setResults([]);
    setTotalResults(0);
    setQueryObject(null);

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const body: any = {
        counterMetricsDisabled,
        neverContactedBrandsOnly,
        alreadyEmailedBrandsDisabled: alreadyEmailedDisabled,
        searchType,
        limit,
        overrideIncludedFilterIds: includedFilterIds,
        overrideExcludedFilterIds: excludedFilterIds,
      };

      if (searchMode === "custom") {
        body.organizationNicheIds = [];
        body.overrideSearchParameters = automationCards.map((c) => ({
          ...c.params,
          category_ids: c.params.category_ids || [],
        }));
      } else {
        body.organizationNicheIds = selectedNicheIds;
      }

      const res = await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users/${organizationId}/search-brands`,
        "POST",
        {},
        body,
      );
      setResults(res.bentoBrands || []);
      setTotalResults(res.totalResults || 0);
      setQueryObject(res.query || null);
    } catch (error: any) {
      setAlert(error?.message || "Search failed", "warning");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNiche = (nicheId: number) => {
    setSelectedNicheIds((prev) =>
      prev.includes(nicheId)
        ? prev.filter((id) => id !== nicheId)
        : [...prev, nicheId],
    );
  };

  const handleSelectAllNiches = () => {
    if (selectedNicheIds.length === niches.length) {
      setSelectedNicheIds([]);
    } else {
      setSelectedNicheIds(niches.map((n) => n.id));
    }
  };

  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ py: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Brand Search</Typography>
          <IconButton onClick={onClose} size="small">
            <Typography variant="body2">X</Typography>
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* ── Toolbar row ── */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            mb: 1.5,
            p: 1.5,
            bgcolor: grey[50],
            borderRadius: 1.5,
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            disabled={
              loading ||
              (searchMode === "niches" && selectedNicheIds.length === 0) ||
              (searchMode === "custom" && automationCards.length === 0)
            }
          >
            {loading ? "Searching..." : "Search"}
          </Button>

          <Divider orientation="vertical" flexItem />

          <ToggleButtonGroup
            value={searchMode}
            exclusive
            onChange={(_, val) => val && handleSwitchMode(val)}
            size="small"
          >
            <ToggleButton value="niches" sx={{ py: 0.25, fontSize: 12 }}>
              Niches
            </ToggleButton>
            <ToggleButton value="custom" sx={{ py: 0.25, fontSize: 12 }}>
              Custom Automations
            </ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            value={searchType}
            exclusive
            onChange={(_, val) => val && setSearchType(val)}
            size="small"
          >
            <ToggleButton value="automations" sx={{ py: 0.25, fontSize: 12 }}>
              Automations
            </ToggleButton>
            <ToggleButton value="repitching" sx={{ py: 0.25, fontSize: 12 }}>
              Repitching
            </ToggleButton>
            <ToggleButton value="saved_brands" sx={{ py: 0.25, fontSize: 12 }}>
              Saved
            </ToggleButton>
          </ToggleButtonGroup>

          <Select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            size="small"
            sx={{ minWidth: 64, "& .MuiSelect-select": { py: 0.5 } }}
          >
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={200}>200</MenuItem>
          </Select>

          <FormControlLabel
            control={
              <Checkbox
                checked={counterMetricsDisabled}
                onChange={(e) => setCounterMetricsDisabled(e.target.checked)}
                size="small"
                sx={{ p: 0.25 }}
              />
            }
            label={<Typography sx={{ fontSize: 12 }}>No Counter</Typography>}
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={alreadyEmailedDisabled}
                onChange={(e) => setAlreadyEmailedDisabled(e.target.checked)}
                size="small"
                sx={{ p: 0.25 }}
              />
            }
            label={<Typography sx={{ fontSize: 12 }}>Inc. Emailed</Typography>}
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={neverContactedBrandsOnly}
                onChange={(e) => setNeverContactedBrandsOnly(e.target.checked)}
                size="small"
                sx={{ p: 0.25 }}
              />
            }
            label={
              <Typography sx={{ fontSize: 12 }}>Never Contacted</Typography>
            }
            sx={{ m: 0 }}
          />

          {allVaSearchFilters.length > 0 && (
            <>
              <Divider orientation="vertical" flexItem />
              <Button
                size="small"
                variant={filtersOpen ? "contained" : "outlined"}
                onClick={() => setFiltersOpen((v) => !v)}
                sx={{ py: 0.25, fontSize: 12, minWidth: 0 }}
              >
                VA Filters{hasFilterOverrides() ? " *" : ""}
              </Button>
            </>
          )}

          {queryObject && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                navigator.clipboard.writeText(
                  JSON.stringify(queryObject, null, 2),
                );
                setAlert("Query copied to clipboard", "success");
              }}
              sx={{ py: 0.25, fontSize: 12, ml: "auto" }}
            >
              Copy Query
            </Button>
          )}
        </Box>

        {/* ── Collapsible VA Search Filters ── */}
        <Collapse in={filtersOpen}>
          <Box
            sx={{
              mb: 1.5,
              p: 1.5,
              border: "1px solid",
              borderColor: grey[200],
              borderRadius: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                VA Search Filters
              </Typography>
              {hasFilterOverrides() && (
                <Button
                  size="small"
                  onClick={resetFilters}
                  sx={{ fontSize: 11, py: 0 }}
                >
                  Reset
                </Button>
              )}
            </Box>

            {includeFilters.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Include
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {includeFilters.map((f) => (
                    <Chip
                      key={f.id}
                      label={f.vaDisplayName || f.name}
                      size="small"
                      color={
                        includedFilterIds.includes(f.id) ? "primary" : "default"
                      }
                      variant={
                        includedFilterIds.includes(f.id) ? "filled" : "outlined"
                      }
                      onClick={() => toggleIncluded(f.id)}
                      sx={{ cursor: "pointer", fontSize: 11, height: 24 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {excludeFilters.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Exclude
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {excludeFilters.map((f) => (
                    <Chip
                      key={f.id}
                      label={f.vaDisplayName || f.name}
                      size="small"
                      color={
                        excludedFilterIds.includes(f.id)
                          ? "secondary"
                          : "default"
                      }
                      variant={
                        excludedFilterIds.includes(f.id) ? "filled" : "outlined"
                      }
                      onClick={() => toggleExcluded(f.id)}
                      sx={{ cursor: "pointer", fontSize: 11, height: 24 }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>

        {/* ── Niche Selection (niches mode) ── */}
        {searchMode === "niches" && (
          <Box sx={{ mb: 1.5 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Niches ({selectedNicheIds.length}/{niches.length})
              </Typography>
              <Button
                size="small"
                onClick={handleSelectAllNiches}
                sx={{ fontSize: 11, py: 0, minWidth: 0 }}
              >
                {selectedNicheIds.length === niches.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {niches.map((niche) => (
                <Chip
                  key={niche.id}
                  label={`${niche.name}${
                    niche.locationName ? ` (${niche.locationName})` : ""
                  }`}
                  size="small"
                  color={
                    selectedNicheIds.includes(niche.id) ? "primary" : "default"
                  }
                  variant={
                    selectedNicheIds.includes(niche.id) ? "filled" : "outlined"
                  }
                  onClick={() => handleToggleNiche(niche.id)}
                  sx={{ cursor: "pointer", fontSize: 12, height: 24 }}
                />
              ))}
              {niches.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No niches available.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* ── Custom Automations (custom mode) ── */}
        {searchMode === "custom" && (
          <Box sx={{ mb: 1.5 }}>
            {automationCards.map((card) => (
              <AutomationCardEditor
                key={card.id}
                card={card}
                categories={categories}
                onPatch={(patch) => updateAutomationCard(card.id, patch)}
                onRemove={() => removeAutomationCard(card.id)}
              />
            ))}
            <Button
              variant="outlined"
              size="small"
              onClick={addAutomationCard}
              sx={{ fontSize: 12 }}
            >
              + Add Automation
            </Button>
          </Box>
        )}

        {/* ── Results ── */}
        {loading ? (
          <Grid container justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Grid>
        ) : results.length > 0 ? (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              {totalResults} results
            </Typography>
            <Grid container spacing={2}>
              {results.map((brand) => (
                <Grid item xs={12} md={6} key={brand.id}>
                  <BrandResultCard brand={brand} />
                </Grid>
              ))}
            </Grid>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// GeopointPicker — Google Places autocomplete + geopoint chips
// ---------------------------------------------------------------------------

function GeopointPicker({
  geopoints,
  onChange,
}: {
  geopoints: string[];
  onChange: (geopoints: string[]) => void;
}) {
  // Always read the latest geopoints inside async callbacks
  const geopointsRef = useRef<string[]>(geopoints);
  useEffect(() => {
    geopointsRef.current = geopoints;
  }, [geopoints]);

  const parsed = useMemo(() => {
    return geopoints
      .map((g) => {
        try {
          return JSON.parse(g) as Geopoint;
        } catch {
          return null;
        }
      })
      .filter(Boolean) as Geopoint[];
  }, [geopoints]);

  const handleLocationChange = (userLocation: UserLocation) => {
    const current = geopointsRef.current || [];
    const currentParsed: Geopoint[] = current
      .map((g) => {
        try {
          return JSON.parse(g) as Geopoint;
        } catch {
          return null;
        }
      })
      .filter(Boolean) as Geopoint[];

    const addressComponents = userLocation?.address_components;

    let city = addressComponents?.find(
      (x) =>
        x?.types?.includes("administrative_area_level_3") ||
        x?.types?.includes("locality"),
    )?.longText;

    const region = addressComponents?.find((x) =>
      x?.types?.includes("administrative_area_level_1"),
    )?.longText;

    let isCountry = false;
    if (!city && !region) {
      city = addressComponents?.[0]?.longText;
      isCountry = true;
    }

    if (
      userLocation?.country_code &&
      userLocation.latitude &&
      userLocation.longitude
    ) {
      const geopoint: Geopoint = {
        country_code: userLocation.country_code,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
        is_country: isCountry,
        label: city || region,
      };

      // Avoid duplicates using the *current* list
      const exists = currentParsed.some(
        (g) =>
          g.latitude === geopoint.latitude &&
          g.longitude === geopoint.longitude,
      );
      if (!exists) {
        onChange([...current, JSON.stringify(geopoint)]);
      }
    }
  };

  const removeGeopoint = (index: number) => {
    const current = geopointsRef.current || [];
    onChange(current.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 10, display: "block", lineHeight: 1.2 }}
      >
        Cities / Countries
      </Typography>
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          border: "1px solid",
          borderColor: grey[300],
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <LocationAutocomplete
          handleLocationChange={handleLocationChange}
          customStyling={{
            border: "none",
            borderRadius: 1,
            height: 28,
            py: 0.25,
            px: 1,
            width: "100%",
            fontSize: 12,
            "&:hover, &:focus": { outline: "none" },
          }}
          placeholderText="Add city or country..."
        />
      </Box>

      {parsed.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
          {parsed.map((gp, i) => (
            <Chip
              key={i}
              label={
                gp.label ? `${gp.label} (${gp.country_code})` : gp.country_code
              }
              size="small"
              onDelete={() => removeGeopoint(i)}
              sx={{ fontSize: 11, height: 22 }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Multi-select helper
// ---------------------------------------------------------------------------

function handleMultiSelectChange(
  current: string[] | undefined,
  newValues: string[],
): string[] {
  // If user selected "all", clear specific values
  if (newValues.includes("all") && !current?.includes("all")) {
    return ["all"];
  }
  // If user selected a specific value while "all" was selected, remove "all"
  const filtered = newValues.filter((v) => v !== "all");
  return filtered.length === 0 ? ["all"] : filtered;
}

// ---------------------------------------------------------------------------
// AutomationCardEditor
// ---------------------------------------------------------------------------

function AutomationCardEditor({
  card,
  categories,
  onPatch,
  onRemove,
}: {
  card: AutomationCard;
  categories: BentoCategory[];
  onPatch: (patch: Partial<BrandSearchParameters>) => void;
  onRemove: () => void;
}) {
  const { params } = card;
  const [expanded, setExpanded] = useState(false);

  const update = (patch: Partial<BrandSearchParameters>) => {
    onPatch(patch);
  };

  const [expandedCats, setExpandedCats] = useState<Set<number>>(new Set());

  const toggleExpandCat = (catId: number) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  const selectedCatIds = new Set(params.category_ids || []);

  const toggleCategory = (catId: number) => {
    const current = params.category_ids || [];
    if (current.includes(catId)) {
      update({ category_ids: current.filter((id) => id !== catId) });
    } else {
      update({ category_ids: [...current, catId] });
    }
  };

  const getFollowingSizeKey = (): string => {
    if (
      params.min_instagram_followers == null &&
      params.max_instagram_followers == null
    )
      return "all";
    for (const [key, range] of Object.entries(FollowingSizeRange)) {
      if (
        range.min === params.min_instagram_followers &&
        range.max === params.max_instagram_followers
      )
        return key;
    }
    return "all";
  };

  const getTargetAgeKey = (): string => {
    if (params.min_target_age == null && params.max_target_age == null)
      return "all";
    for (const [key, range] of Object.entries(TargetAgeRange)) {
      if (
        range.min === params.min_target_age &&
        range.max === params.max_target_age
      )
        return key;
    }
    return "all";
  };

  // Build compact summary chips for the collapsed header
  const summaryChips: string[] = [];
  if (params.query) summaryChips.push(`"${params.query}"`);
  if (
    params.company_types &&
    !params.company_types.includes("all") &&
    params.company_types.length > 0
  )
    summaryChips.push(`${params.company_types.length} co. types`);
  if (
    params.locations &&
    !params.locations.includes("all") &&
    params.locations.length > 0
  )
    summaryChips.push(`${params.locations.length} locations`);
  if (params.geopoints && params.geopoints.length > 0)
    summaryChips.push(`${params.geopoints.length} geopoints`);
  if (selectedCatIds.size > 0)
    summaryChips.push(`${selectedCatIds.size} categories`);
  if (params.compensation_types && params.compensation_types.length > 0)
    summaryChips.push(`${params.compensation_types.length} comp.`);
  if (getFollowingSizeKey() !== "all") summaryChips.push("IG filter");
  if (getTargetAgeKey() !== "all") summaryChips.push("Age filter");
  if (params.target_genders && params.target_genders.length > 0)
    summaryChips.push("Gender filter");

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: expanded ? grey[400] : grey[200],
        borderRadius: 1.5,
        mb: 1,
        overflow: "hidden",
        transition: "border-color 0.15s",
      }}
    >
      {/* ── Collapsed header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          cursor: "pointer",
          bgcolor: expanded ? grey[50] : "transparent",
          "&:hover": { bgcolor: grey[50] },
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, flexShrink: 0, fontSize: 12 }}
        >
          {card.nicheName || "Custom"}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {summaryChips.map((label) => (
            <Chip
              key={label}
              label={label}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10, height: 20 }}
            />
          ))}
          {summaryChips.length === 0 && (
            <Typography variant="caption" color="text.disabled">
              no filters
            </Typography>
          )}
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flexShrink: 0 }}
        >
          {expanded ? "▾" : "▸"}
        </Typography>

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          sx={{ p: 0.25, flexShrink: 0 }}
        >
          <Typography variant="caption" color="error" sx={{ fontSize: 11 }}>
            ✕
          </Typography>
        </IconButton>
      </Box>

      {/* ── Expanded editor ── */}
      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, pb: 1.5, pt: 1 }}>
          {/* Query */}
          <TextField
            placeholder="Search query"
            value={params.query || ""}
            onChange={(e) => update({ query: e.target.value || undefined })}
            size="small"
            fullWidth
            sx={{ mb: 1 }}
            InputProps={{ sx: { fontSize: 13 } }}
          />

          {/* Filter grid — two rows of compact dropdowns */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 1,
              mb: 1,
            }}
          >
            <CompactSelect
              label="Company"
              multiple
              value={params.company_types || ["all"]}
              onChange={(v) =>
                update({
                  company_types: handleMultiSelectChange(
                    params.company_types,
                    v as string[],
                  ),
                })
              }
              options={COMPANY_TYPES.map((ct) => ({
                value: ct.key,
                label: ct.name,
              }))}
              renderValue={(sel) =>
                (sel as string[]).includes("all")
                  ? "All"
                  : `${(sel as string[]).length} sel.`
              }
            />

            <CompactSelect
              label="Contact"
              multiple
              value={params.contact_types || ["all"]}
              onChange={(v) =>
                update({
                  contact_types: handleMultiSelectChange(
                    params.contact_types,
                    v as string[],
                  ),
                })
              }
              options={CONTACT_TYPES.map((ct) => ({
                value: ct.key,
                label: ct.name,
              }))}
              renderValue={(sel) =>
                (sel as string[]).includes("all")
                  ? "All"
                  : `${(sel as string[]).length} sel.`
              }
            />

            <CompactSelect
              label="Location"
              multiple
              value={params.locations || ["all"]}
              onChange={(v) =>
                update({
                  locations: handleMultiSelectChange(
                    params.locations,
                    v as string[],
                  ),
                })
              }
              options={LOCATIONS.map((loc) => ({
                value: loc.key,
                label: loc.name,
              }))}
              renderValue={(sel) =>
                (sel as string[]).includes("all")
                  ? "All"
                  : `${(sel as string[]).length} sel.`
              }
            />

            <CompactSelect
              label="IG Following"
              value={getFollowingSizeKey()}
              onChange={(v) => {
                const key = v as string;
                if (key === "all") {
                  update({
                    min_instagram_followers: undefined,
                    max_instagram_followers: undefined,
                  });
                } else {
                  const range = FollowingSizeRange[key];
                  update({
                    min_instagram_followers: range.min,
                    max_instagram_followers: range.max,
                  });
                }
              }}
              options={FOLLOWING_SIZES.map((fs) => ({
                value: fs.key,
                label: fs.name,
              }))}
            />

            <CompactSelect
              label="Compensation"
              multiple
              value={params.compensation_types || []}
              onChange={(v) =>
                update({
                  compensation_types:
                    (v as string[]).length === 0 ? undefined : (v as string[]),
                })
              }
              options={COMPENSATION_TYPES.map((ct) => ({
                value: ct.key,
                label: ct.name,
              }))}
              renderValue={(sel) =>
                (sel as string[]).length === 0
                  ? "Any"
                  : `${(sel as string[]).length} sel.`
              }
              displayEmpty
            />

            <CompactSelect
              label="Gender"
              multiple
              value={params.target_genders || []}
              onChange={(v) =>
                update({
                  target_genders:
                    (v as string[]).length === 0 ? undefined : (v as string[]),
                })
              }
              options={TARGET_GENDERS.filter((g) => g.key !== "all").map(
                (g) => ({
                  value: g.key,
                  label: g.name,
                }),
              )}
              renderValue={(sel) =>
                (sel as string[]).length === 0
                  ? "Any"
                  : `${(sel as string[]).length} sel.`
              }
              displayEmpty
            />

            <CompactSelect
              label="Age"
              value={getTargetAgeKey()}
              onChange={(v) => {
                const key = v as string;
                if (key === "all") {
                  update({
                    min_target_age: undefined,
                    max_target_age: undefined,
                  });
                } else {
                  const range = TargetAgeRange[key];
                  update({
                    min_target_age: range.min,
                    max_target_age: range.max,
                  });
                }
              }}
              options={TARGET_AGE_RANGES.map((age) => ({
                value: age.key,
                label: age.name,
              }))}
            />
          </Box>

          {/* Geopoints */}
          <GeopointPicker
            geopoints={params.geopoints || []}
            onChange={(geopoints) =>
              update({
                geopoints: geopoints.length > 0 ? geopoints : undefined,
              })
            }
          />

          {/* Categories */}
          {categories.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
              >
                Categories ({selectedCatIds.size})
              </Typography>
              <Box
                sx={{
                  maxHeight: 200,
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: grey[200],
                  borderRadius: 1,
                  p: 0.75,
                }}
              >
                {categories.map((topCat) => (
                  <Box key={topCat.id}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        "&:hover": { bgcolor: grey[50] },
                        borderRadius: 0.5,
                        px: 0.5,
                        py: 0.125,
                      }}
                      onClick={() => toggleExpandCat(topCat.id)}
                    >
                      <Typography
                        sx={{ fontWeight: 600, flex: 1, fontSize: 12 }}
                      >
                        {topCat.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: 10 }}
                      >
                        {expandedCats.has(topCat.id) ? "−" : "+"}
                      </Typography>
                    </Box>
                    {expandedCats.has(topCat.id) &&
                      topCat.subcategories &&
                      topCat.subcategories.length > 0 && (
                        <Box
                          sx={{
                            pl: 1.5,
                            display: "flex",
                            flexWrap: "wrap",
                          }}
                        >
                          {topCat.subcategories.map((sub) => (
                            <FormControlLabel
                              key={sub.id}
                              control={
                                <Checkbox
                                  checked={selectedCatIds.has(sub.id)}
                                  onChange={() => toggleCategory(sub.id)}
                                  size="small"
                                  sx={{ p: 0.25 }}
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: 11 }}>
                                  {sub.displayName || sub.name}
                                </Typography>
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mr: 1.5,
                                my: 0,
                                minWidth: 0,
                              }}
                            />
                          ))}
                        </Box>
                      )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CompactSelect — tiny labeled select for the filter grid
// ---------------------------------------------------------------------------

function CompactSelect({
  label,
  multiple,
  value,
  onChange,
  options,
  renderValue,
  displayEmpty,
}: {
  label: string;
  multiple?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void;
  options: { value: string; label: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderValue?: (value: any) => React.ReactNode;
  displayEmpty?: boolean;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 10, display: "block", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Select
        multiple={multiple}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        displayEmpty={displayEmpty}
        renderValue={renderValue}
        sx={{ "& .MuiSelect-select": { py: 0.5, fontSize: 12 } }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Stat pill
// ---------------------------------------------------------------------------

function StatValue({
  label,
  value,
  color,
}: {
  label: string;
  value: React.ReactNode;
  color?: string;
}) {
  return (
    <Box sx={{ textAlign: "center", minWidth: 48 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          lineHeight: 1.1,
          color: color || "text.primary",
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 10, lineHeight: 1.2 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Brand Card
// ---------------------------------------------------------------------------

function BrandResultCard({ brand }: { brand: SearchBrand }) {
  const [responsesOpen, setResponsesOpen] = useState(false);
  const [igProfileOpen, setIgProfileOpen] = useState(false);

  const contacts = brand.contacts || [];
  const partnerships = brand.partnerships || [];
  const recentResponses = brand.recentResponses || [];

  const contactedTotal = brand.contactedAmount ?? 0;
  const contactedRecently = brand.contactedRecentlyAmount ?? 0;
  const nicheness = brand.nicheness;
  const esScore = brand.Score;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: grey[200],
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: 2 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 2,
          pb: 1,
        }}
      >
        {brand.logoUrl ? (
          <Box
            component="img"
            src={brand.logoUrl}
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: grey[200],
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        ) : (
          <Avatar
            sx={{
              bgcolor: green[700],
              width: 36,
              height: 36,
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            {generateInitials(brand.brandName)}
          </Avatar>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, lineHeight: 1.3 }}
            noWrap
          >
            {brand.brandName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontSize: 11 }}
            >
              #{brand.id}
            </Typography>
            {brand.createdAt && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: 11 }}
              >
                · {new Date(brand.createdAt * 1000).toLocaleDateString()}
              </Typography>
            )}
            {brand.instagramFollowerCount != null && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 11 }}
              >
                · {formatNumber(brand.instagramFollowerCount)}
              </Typography>
            )}
            {brand.instagramUsername && (
              <>
                <Link
                  href={`https://instagram.com/${brand.instagramUsername}`}
                  target="_blank"
                  sx={{ fontSize: 11 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  @{brand.instagramUsername}
                </Link>
                <IconButton
                  size="small"
                  onClick={() => setIgProfileOpen(true)}
                  sx={{ p: 0.25, fontSize: 12, color: grey[500] }}
                >
                  <Box
                    component="i"
                    className="fa-solid fa-circle-info"
                    sx={{ fontSize: 11 }}
                  />
                </IconButton>
              </>
            )}
            {brand.website && (
              <>
                <Typography variant="caption" color="text.secondary">
                  |
                </Typography>
                <Link
                  href={`https://${brand.website}`}
                  target="_blank"
                  sx={{ fontSize: 11 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {brand.website.split("/")[0]}
                </Link>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Stat Row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          px: 2,
          py: 1,
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: grey[100],
          bgcolor: grey[50],
        }}
      >
        <StatValue
          label="ES Score"
          value={esScore != null ? Math.round(esScore) : "-"}
          color={blue[700]}
        />
        <StatValue
          label="Nicheness"
          value={nicheness != null ? nicheness.toFixed(1) : "-"}
          color={
            nicheness != null
              ? nicheness >= 7
                ? green[700]
                : nicheness >= 4
                  ? orange[800]
                  : grey[600]
              : undefined
          }
        />
        <StatValue
          label="Contacted"
          value={contactedTotal}
          color={contactedTotal > 6 ? orange[800] : undefined}
        />
        <StatValue
          label="Recent"
          value={contactedRecently}
          color={contactedRecently > 3 ? orange[800] : undefined}
        />
        <StatValue label="Contacts" value={contacts.length} />
        <StatValue label="Partnerships" value={partnerships.length} />
      </Box>

      {/* Brand Flags */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          px: 2,
          py: 1,
        }}
      >
        {brand.worksWithUgc && (
          <Chip
            label="UGC"
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: 11, height: 22 }}
          />
        )}
        {brand.worksWithInfluencer && (
          <Chip
            label="Influencer"
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: 11, height: 22 }}
          />
        )}
        {brand.hasValidatedPartnership && (
          <Chip
            label="Validated Partnership"
            size="small"
            color="info"
            variant="outlined"
            sx={{ fontSize: 11, height: 22 }}
          />
        )}
        {brand.compensationTypes?.map((ct) => (
          <Chip
            key={ct}
            label={ct.replace(/_/g, " ")}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: 11, height: 22, textTransform: "capitalize" }}
          />
        ))}
        {brand.emailedBrand && (
          <Chip
            label="Pitched"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontSize: 11, height: 22 }}
          />
        )}
      </Box>

      {/* Metadata Tags */}
      {brand.metadata && brand.metadata.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            px: 2,
            pb: 1,
          }}
        >
          {brand.metadata.map((tag, i) => (
            <Chip
              key={`m-${i}`}
              label={tag.value}
              size="small"
              sx={{
                fontSize: 11,
                height: 22,
                bgcolor: "white",
                border: `0.5px solid ${
                  tag.type === "location" ? grey[900] : grey[500]
                }`,
                fontWeight: tag.type === "location" ? 600 : 400,
              }}
            />
          ))}
        </Box>
      )}

      {/* Location */}
      <LocationSection brand={brand} />

      {/* About */}
      {brand.aboutSummary && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            {brand.aboutSummary}
          </Typography>
        </Box>
      )}

      {/* Recent Responses */}
      <Box
        sx={{ px: 2, py: 1, borderTop: "1px solid", borderColor: grey[100] }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
        >
          Recent Responses ({recentResponses.length})
        </Typography>
        {recentResponses.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              alignItems: "center",
            }}
          >
            {recentResponses.slice(0, 8).map((r, i) => (
              <Chip
                key={i}
                label={`${r.best_response_type} (${new Date(
                  r.timestamp * 1000,
                ).toLocaleDateString()})`}
                size="small"
                color={getResponseColor(r.best_response_type)}
                variant="outlined"
                sx={{ fontSize: 10, height: 20 }}
              />
            ))}
            <Typography
              variant="caption"
              color="primary"
              sx={{
                cursor: "pointer",
                fontSize: 11,
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => setResponsesOpen(true)}
            >
              See all
            </Typography>
          </Box>
        ) : (
          <Typography
            variant="caption"
            color="primary"
            sx={{
              cursor: "pointer",
              fontSize: 11,
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => setResponsesOpen(true)}
          >
            See responses
          </Typography>
        )}
      </Box>

      <ResponsesDialog
        open={responsesOpen}
        onClose={() => setResponsesOpen(false)}
        bentoBrandId={brand.id}
        brandName={brand.brandName}
      />

      {brand.instagramUsername && (
        <InstagramProfileDialog
          open={igProfileOpen}
          onClose={() => setIgProfileOpen(false)}
          username={brand.instagramUsername}
        />
      )}

      <Divider />

      {/* Contacts Table */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
        >
          Contacts ({contacts.length})
        </Typography>
        {contacts.length > 0 ? (
          <TableContainer
            sx={{
              border: "1px solid",
              borderColor: grey[200],
              borderRadius: 1,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: grey[50] }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                    Title
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                    Source
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                    Best Response
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                    Last Sent
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell sx={{ fontSize: 12 }}>
                      {contact.name || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {contact.email || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {contact.title || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {contact.source || "-"}
                    </TableCell>
                    <TableCell>
                      {contact.best_response_type ? (
                        <Chip
                          label={contact.best_response_type}
                          size="small"
                          color={getResponseColor(contact.best_response_type)}
                          variant="outlined"
                          sx={{ fontSize: 10, height: 20 }}
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {contact.last_sent_to_at
                        ? new Date(
                            contact.last_sent_to_at * 1000,
                          ).toLocaleDateString()
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            No contacts.
          </Typography>
        )}
      </Box>

      {/* Partnerships */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
        >
          Partnerships ({partnerships.length})
        </Typography>
        {partnerships.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {partnerships.map((p, i) => (
              <PartnershipChip key={i} partnership={p} />
            ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            No partnerships.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Instagram Profile Dialog
// ---------------------------------------------------------------------------

interface RelatedProfile {
  full_name: string;
  id: string;
  is_verified: boolean;
  username: string;
  profile_pic_url?: string;
}

interface BentoBrandContactData {
  id: number;
  isArchived: boolean;
  [key: string]: unknown;
}

interface BentoBrandData {
  id: number;
  isArchived: boolean;
  brandName: string;
  normalizedName: string;
  instagramUsername: string | null;
  bentoContacts: BentoBrandContactData[];
  [key: string]: unknown;
}

interface SocialMediaAccountData {
  id: number;
  username: string;
  name: string | null;
  accountCategory: string | null;
  businessCategory: string | null;
  description: string | null;
  numOfFollowers: number | null;
  numOfFollowing: number | null;
  numOfPosts: number | null;
  profilePicUrl: string | null;
  assetUrls: string[] | null;
  businessAddressJson: Record<string, string> | null;
  createdAt: number | null;
  relatedProfilesProcessed: boolean;
  relatedProfiles: { edges: { node: RelatedProfile }[] } | null;
  relatedProfilesLastProcessedAt: number | null;
  worksWithUgc: boolean | null;
  worksWithInfluencer: boolean | null;
  hasValidatedPartnership: boolean | null;
  isVerified: boolean | null;
  isBusinessAccount: boolean | null;
  externalUrl: string | null;
  publicEmail: string | null;
}

function InstagramProfileDialog({
  open,
  onClose,
  username,
}: {
  open: boolean;
  onClose: () => void;
  username: string;
}) {
  const { getToken } = useAuth();
  const [account, setAccount] = useState<SocialMediaAccountData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [relatedBrands, setRelatedBrands] = useState<
    Record<string, BentoBrandData | null>
  >({});
  const [relatedBrandsLoading, setRelatedBrandsLoading] = useState(false);

  const fetchRelatedBrands = async (profiles: RelatedProfile[]) => {
    const usernames = profiles.map((p) => p.username).filter(Boolean);
    if (usernames.length === 0) return;
    setRelatedBrandsLoading(true);
    try {
      const params = new URLSearchParams();
      usernames.forEach((u) => params.append("usernames", u));
      const res = await fetcherAuth(
        getToken,
        `/api/admin/bento-brands/instagram-usernames?${params.toString()}`,
        "GET",
      );
      setRelatedBrands(res);
    } catch {
      setRelatedBrands({});
    } finally {
      setRelatedBrandsLoading(false);
    }
  };

  const fetchAccount = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/social-media-accounts/username/${username}`,
        "GET",
      );
      setAccount(res.account);
      // Fetch bento brand data for related profiles
      const edges = res.account?.relatedProfiles?.edges;
      if (edges && edges.length > 0) {
        fetchRelatedBrands(edges.map((e: { node: RelatedProfile }) => e.node));
      }
    } catch (err: any) {
      setError(err?.message || "Account not found");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    if (!account && !error) {
      fetchAccount();
    }
  };

  const formatAddress = (addr: Record<string, string> | null) => {
    if (!addr) return null;
    const parts = [
      addr.street_address,
      addr.city_name,
      addr.region_name,
      addr.zip_code,
      addr.country_code,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  const assets = (account?.assetUrls || []).slice(0, 6);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEnter: handleOpen }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Link
            href={`https://instagram.com/${username}`}
            target="_blank"
            sx={{ fontSize: 11 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6">@{username}</Typography>
          </Link>

          <IconButton onClick={onClose}>
            <Typography variant="body2">X</Typography>
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Grid container justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Grid>
        ) : error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : account ? (
          <Box>
            {/* Profile header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 2,
              }}
            >
              {account.profilePicUrl ? (
                <Box
                  component="img"
                  src={account.profilePicUrl}
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    border: "2px solid",
                    borderColor: grey[200],
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Avatar sx={{ width: 64, height: 64, bgcolor: grey[400] }}>
                  {username[0]?.toUpperCase()}
                </Avatar>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {account.name || username}
                  </Typography>
                  {account.isVerified && (
                    <Box
                      component="i"
                      className="fa-solid fa-circle-check"
                      sx={{ color: blue[500], fontSize: 14 }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {account.accountCategory || account.businessCategory || ""}
                </Typography>
              </Box>
            </Box>

            {/* Stats row */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-around",
                py: 1.5,
                mb: 2,
                borderTop: "1px solid",
                borderBottom: "1px solid",
                borderColor: grey[200],
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {account.numOfPosts != null
                    ? formatNumber(account.numOfPosts)
                    : "-"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Posts
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {account.numOfFollowers != null
                    ? formatNumber(account.numOfFollowers)
                    : "-"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Followers
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {account.numOfFollowing != null
                    ? formatNumber(account.numOfFollowing)
                    : "-"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Following
                </Typography>
              </Box>
            </Box>

            {/* Bio */}
            {account.description && (
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {account.description}
              </Typography>
            )}

            {account.externalUrl && (
              <Link
                href={
                  account.externalUrl.startsWith("http")
                    ? account.externalUrl
                    : `https://${account.externalUrl}`
                }
                target="_blank"
                sx={{ fontSize: 13, display: "block" }}
              >
                {account.externalUrl}
              </Link>
            )}

            {formatAddress(account.businessAddressJson) && (
              <Typography variant="caption" color="textSecondary">
                {formatAddress(account.businessAddressJson)}
              </Typography>
            )}

            {/* Asset grid */}
            {assets.length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 0.5,
                  my: 2,
                }}
              >
                {assets.map((url, i) => (
                  <Box
                    key={i}
                    component="img"
                    src={url}
                    sx={{
                      width: "100%",
                      aspectRatio: "1",
                      objectFit: "cover",
                      borderRadius: 0.5,
                      bgcolor: grey[100],
                    }}
                  />
                ))}
              </Box>
            )}

            <Divider sx={{ my: 1.5 }} />

            {/* Metadata */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, display: "block", mb: 1 }}
            >
              Details
            </Typography>

            {/* Flags */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                mb: 1.5,
              }}
            >
              {account.worksWithUgc && (
                <Chip
                  label="Works with UGC"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: 11, height: 22 }}
                />
              )}
              {account.worksWithInfluencer && (
                <Chip
                  label="Works with Influencer"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: 11, height: 22 }}
                />
              )}
              {account.hasValidatedPartnership && (
                <Chip
                  label="Validated Partnership"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ fontSize: 11, height: 22 }}
                />
              )}
              {account.isBusinessAccount && (
                <Chip
                  label="Business Account"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 11, height: 22 }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: 0.5,
                rowGap: 0.25,
                fontSize: 13,
              }}
            >
              {account.publicEmail && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="caption">
                    {account.publicEmail}
                  </Typography>
                </>
              )}
              <Typography variant="caption" color="text.secondary">
                Created
              </Typography>
              <Typography variant="caption">
                {account.createdAt
                  ? new Date(account.createdAt * 1000).toLocaleDateString()
                  : "-"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Related Profiles Processed
              </Typography>
              <Typography variant="caption">
                {account.relatedProfilesProcessed ? "true" : "false"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Processed At
              </Typography>
              <Typography variant="caption">
                {account.relatedProfilesLastProcessedAt
                  ? `Processed${
                      account.relatedProfilesLastProcessedAt
                        ? ` (${new Date(
                            account.relatedProfilesLastProcessedAt * 1000,
                          ).toLocaleDateString()})`
                        : ""
                    }`
                  : "N/A"}
              </Typography>
            </Box>

            {/* Related Profiles */}
            {account.relatedProfiles?.edges &&
              account.relatedProfiles.edges.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, display: "block", mb: 1 }}
                  >
                    Related Profiles ({account.relatedProfiles.edges.length})
                  </Typography>
                  {relatedBrandsLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 2 }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                  ) : (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {account.relatedProfiles.edges.map((edge) => {
                        const profile = edge.node;
                        const brandData = relatedBrands[profile.username];
                        const unarchivedContacts =
                          brandData?.bentoContacts?.filter(
                            (c) => !c.isArchived,
                          ) || [];
                        return (
                          <Box
                            key={profile.id || profile.username}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              p: 1,
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: grey[200],
                              bgcolor: grey[50],
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                fontSize: 14,
                                bgcolor: grey[400],
                              }}
                            >
                              {profile.username?.[0]?.toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Link
                                  href={`https://instagram.com/${profile.username}`}
                                  target="_blank"
                                  sx={{ fontSize: 12, fontWeight: 600 }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  @{profile.username}
                                </Link>
                                {profile.is_verified && (
                                  <Box
                                    component="i"
                                    className="fa-solid fa-circle-check"
                                    sx={{ color: blue[500], fontSize: 11 }}
                                  />
                                )}
                              </Box>
                              {profile.full_name && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", fontSize: 11 }}
                                >
                                  {profile.full_name}
                                </Typography>
                              )}
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: 0.25,
                                flexShrink: 0,
                              }}
                            >
                              {brandData ? (
                                <>
                                  <Chip
                                    label={
                                      brandData.isArchived
                                        ? "Brand (archived)"
                                        : "Brand"
                                    }
                                    size="small"
                                    color={
                                      brandData.isArchived
                                        ? "default"
                                        : "success"
                                    }
                                    variant="outlined"
                                    sx={{ fontSize: 10, height: 20 }}
                                  />
                                  {!brandData.isArchived &&
                                    unarchivedContacts.length > 0 && (
                                      <Typography
                                        variant="caption"
                                        sx={{ fontSize: 10 }}
                                        color="text.secondary"
                                      >
                                        {unarchivedContacts.length} contact
                                        {unarchivedContacts.length !== 1
                                          ? "s"
                                          : ""}
                                      </Typography>
                                    )}
                                </>
                              ) : (
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: 10 }}
                                  color="text.secondary"
                                >
                                  No brand
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </>
              )}
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Responses Dialog
// ---------------------------------------------------------------------------

function ResponsesDialog({
  open,
  onClose,
  bentoBrandId,
  brandName,
}: {
  open: boolean;
  onClose: () => void;
  bentoBrandId: number;
  brandName: string;
}) {
  const { getToken } = useAuth();
  const [responses, setResponses] = useState<BentoContactResponse[] | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [selectedResponse, setSelectedResponse] =
    useState<BentoContactResponse | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [responseLoading, setResponseLoading] = useState(false);

  const fetchResponses = async () => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/bento-brands/${bentoBrandId}/responses?page=1&per_page=50`,
        "GET",
      );
      setResponses(res.bentoContactResponses || []);
    } catch {
      setResponses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchResponse = async (response: BentoContactResponse) => {
    setSelectedResponse(response);
    setResponseLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/bento-contacts/responses/${response.id}`,
        "GET",
      );
      setMessages(res.messages || []);
    } catch {
      setMessages([]);
    } finally {
      setResponseLoading(false);
    }
  };

  const openAttachment = async (
    messageId: string,
    attachment: MessageAttachment,
  ) => {
    if (!selectedResponse?.userIntegrationId) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/user-integrations/${selectedResponse.userIntegrationId}/messages/${messageId}/attachments/${attachment.attachment_id}`,
        "GET",
      );
      openBase64DataInTab(res.attachment.data, attachment.mime_type);
    } catch {
      // ignore
    }
  };

  const handleOpen = () => {
    if (!responses) {
      fetchResponses();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionProps={{ onEnter: handleOpen }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Responses — {brandName}</Typography>
          <IconButton onClick={onClose}>
            <Typography variant="body2">X</Typography>
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Grid container justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Grid>
        ) : responses && responses.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No responses found for this brand.
          </Typography>
        ) : responses ? (
          <Grid container spacing={2}>
            {/* Response list */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  maxHeight: 600,
                  overflow: "auto",
                }}
              >
                {responses.map((res) => (
                  <Card
                    key={res.id}
                    variant="outlined"
                    sx={{
                      borderColor:
                        selectedResponse?.id === res.id
                          ? "primary.main"
                          : "divider",
                    }}
                  >
                    <CardContent sx={{ pb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {res.userIntegration?.email || "Unknown"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        User ID: {res.userIntegration?.userId || "-"}
                      </Typography>
                      {res.responseTypes && res.responseTypes.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          {res.responseTypes.map((rt, i) => (
                            <Chip
                              key={i}
                              label={rt}
                              size="small"
                              color={getResponseColor(rt)}
                              variant="outlined"
                              sx={{ fontSize: 10, height: 20 }}
                            />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                    <CardActions sx={{ pt: 0 }}>
                      <Button
                        size="small"
                        onClick={() => fetchResponse(res)}
                        disabled={responseLoading}
                      >
                        View
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </Box>
            </Grid>

            {/* Email thread */}
            <Grid item xs={12} md={8}>
              {responseLoading ? (
                <Grid container justifyContent="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </Grid>
              ) : messages.length > 0 ? (
                <EmailThread
                  messages={messages}
                  openAttachment={openAttachment}
                />
              ) : selectedResponse ? (
                <Typography variant="body2" color="text.secondary">
                  No messages found for this response.
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a response to view the email thread.
                </Typography>
              )}
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Location section
// ---------------------------------------------------------------------------

function LocationSection({ brand }: { brand: SearchBrand }) {
  const locations = brand.locationNames || [];
  const countries = brand.countries || [];
  const continents = brand.continents || [];
  const hasLocation =
    locations.length > 0 || countries.length > 0 || continents.length > 0;
  const hasScope = brand.isInternational || brand.isLocalOnly;

  if (!hasLocation && !hasScope) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 0.75,
        borderTop: "1px solid",
        borderColor: grey[100],
        bgcolor: grey[50],
        fontSize: 12,
      }}
    >
      <Box
        component="i"
        className="fa-thin fa-location-dot"
        sx={{ color: grey[600], fontSize: 13 }}
      />

      {brand.isInternational && (
        <Chip
          label="International"
          size="small"
          sx={{
            fontSize: 11,
            height: 20,
            bgcolor: blue[50],
            color: blue[800],
            borderColor: blue[200],
          }}
          variant="outlined"
        />
      )}
      {brand.isLocalOnly && (
        <Chip
          label="Local Only"
          size="small"
          sx={{
            fontSize: 11,
            height: 20,
            bgcolor: orange[50],
            color: orange[900],
            borderColor: orange[200],
          }}
          variant="outlined"
        />
      )}

      {locations.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {locations.join(", ")}
        </Typography>
      )}

      {locations.length === 0 && countries.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {countries.join(", ")}
        </Typography>
      )}

      {continents.length > 0 && (
        <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
          ({continents.join(", ")})
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Partnership chip
// ---------------------------------------------------------------------------

function PartnershipChip({
  partnership,
}: {
  partnership: BentoBrandPartnership;
}) {
  const compTypes = Array.isArray(partnership.compensation_types)
    ? partnership.compensation_types.join(", ")
    : partnership.compensation_types || "";
  const country = partnership.country || partnership.organization_country_code;
  const status = partnership.status;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: grey[200],
        borderRadius: 1,
        p: 1,
        fontSize: 12,
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        bgcolor: grey[50],
      }}
    >
      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
        <Chip
          label={status}
          size="small"
          color={
            status === "active"
              ? "success"
              : status === "completed"
                ? "info"
                : "default"
          }
          variant="outlined"
          sx={{ fontSize: 10, height: 20 }}
        />
        {country && (
          <Typography variant="caption" color="text.secondary">
            {country}
          </Typography>
        )}
      </Box>
      {compTypes && (
        <Typography variant="caption" color="text.secondary">
          {compTypes}
        </Typography>
      )}
      {partnership.created_at && (
        <Typography variant="caption" color="text.secondary">
          {new Date(partnership.created_at * 1000).toLocaleDateString()}
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getResponseColor(
  responseType: string,
): "success" | "warning" | "error" | "default" {
  const lower = responseType.toLowerCase();
  if (lower === "positive" || lower === "interested") return "success";
  if (lower === "negative" || lower === "not_interested") return "error";
  if (lower === "maybe" || lower === "follow_up") return "warning";
  return "default";
}
